import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Shield, Flag } from "lucide-react";

const CATEGORY_LABELS = {
  spam: "Spam",
  inappropriate: "Inappropriate",
  fraud: "Fraud",
  no_show: "No-show",
  safety: "Safety",
  other: "Other",
};

const STATUS_TONE = {
  open: "bg-[#C05A46]/10 text-[#C05A46]",
  resolved: "bg-[#8A9A86]/20 text-[#5C5C5C]",
  dismissed: "bg-[#F5F2EB] text-[#5C5C5C]",
};

export default function AdminReports({ onOpenCountChange }) {
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState("open");
  const [selected, setSelected] = useState(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [note, setNote] = useState("");
  const [action, setAction] = useState("resolved");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/reports", { params: status ? { status } : {} })
      .then(({ data }) => {
        setReports(data);
        if (onOpenCountChange) {
          const open = data.filter(r => r.status === "open").length;
          onOpenCountChange(open);
        }
      })
      .catch(() => setReports([]));
  };

  // Always fetch the open count separately for the badge.
  const refreshOpenCount = () => {
    if (!onOpenCountChange) return;
    api.get("/admin/reports", { params: { status: "open" } })
      .then(({ data }) => onOpenCountChange(data.length))
      .catch(() => {});
  };

  useEffect(() => { load(); refreshOpenCount(); }, [status]);

  const doResolve = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/admin/reports/${selected.report_id}/resolve`, {
        action, resolution_note: note,
      });
      toast.success(`Report ${action}`);
      setResolveOpen(false);
      setNote("");
      setAction("resolved");
      setSelected(null);
      load();
      refreshOpenCount();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-reports-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="eyebrow mb-1">Trust &amp; safety</div>
          <div className="font-serif text-2xl">Reports queue</div>
        </div>
        <div className="w-48">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="reports-status-filter" className="bg-white h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
              <SelectItem value="all">All statuses</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="card-elegant p-6 text-sm text-[#5C5C5C]" data-testid="reports-empty">
          <Flag className="w-4 h-4 inline mr-2" />
          {status === "open" ? "Nothing to review right now — the queue is clear." : "No reports match this filter."}
        </div>
      ) : (
        <div className="space-y-3">
          {reports
            .filter(r => status === "all" ? true : r.status === status)
            .map(r => (
              <div key={r.report_id} className="card-elegant p-5" data-testid={`admin-report-${r.report_id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${STATUS_TONE[r.status]}`}>
                        {r.status}
                      </span>
                      <span className="text-[11px] uppercase tracking-widest text-[#5C5C5C]">
                        {CATEGORY_LABELS[r.category] || r.category}
                      </span>
                      <span className="text-[11px] text-[#5C5C5C]">
                        {r.target_type === "vendor" ? "Vendor" : "Planner"} • {new Date(r.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="font-serif text-xl mt-2">{r.target_label || r.target_id}</div>
                    <div className="text-xs text-[#5C5C5C]">
                      Reported by {r.reporter_name || r.reporter_email || r.reporter_id} ({r.reporter_role})
                    </div>
                    <p className="text-sm mt-3 whitespace-pre-wrap max-w-3xl">{r.description}</p>
                    {r.booking_id && (
                      <div className="text-[11px] text-[#5C5C5C] mt-2">
                        Booking reference: <a href={`/bookings/${r.booking_id}`} className="link-underline">{r.booking_id}</a>
                      </div>
                    )}
                    {r.resolution_note && (
                      <div className="text-[11px] text-[#5C5C5C] mt-2 italic">
                        Resolution note: {r.resolution_note}
                      </div>
                    )}
                  </div>
                  {r.status === "open" && (
                    <div className="flex gap-2">
                      <Button
                        data-testid={`report-resolve-${r.report_id}`}
                        variant="outline"
                        className="rounded-full"
                        onClick={() => { setSelected(r); setAction("resolved"); setResolveOpen(true); }}
                      >
                        <Shield className="w-4 h-4 mr-2" /> Take action
                      </Button>
                      <Button
                        data-testid={`report-dismiss-${r.report_id}`}
                        variant="ghost"
                        className="rounded-full"
                        onClick={() => { setSelected(r); setAction("dismissed"); setResolveOpen(true); }}
                      >
                        Dismiss
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}

      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent data-testid="report-resolve-dialog" className="max-w-md bg-[#FDFBF7]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {action === "dismissed" ? "Dismiss report" : "Resolve report"}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5C5C5C]">
              {action === "dismissed"
                ? "Close this report as not actionable. Reporter is not notified."
                : "Close this report as actioned. You may want to suspend the reported account from the Users tab if this represents a policy violation."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="eyebrow mb-2 block">Internal note (optional)</label>
              <Textarea
                data-testid="report-resolve-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add context for the audit trail"
                className="bg-white min-h-[100px]"
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveOpen(false)} disabled={busy} className="rounded-full">Cancel</Button>
            <Button
              onClick={doResolve}
              disabled={busy}
              className="btn-sage rounded-full"
              data-testid="report-resolve-confirm"
            >
              {busy ? "Saving…" : (action === "dismissed" ? "Dismiss report" : "Mark resolved")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
